import { ROUTES } from "@/constants/routes"
import { FAQPage, WithContext } from "schema-dts"

export const jsonLd: WithContext<FAQPage> = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "I need help, where can I get support?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "For the quickest responses you can join our Discord channel. Alternatively, you can email our support at support@example.com where you will get a response within 24 hours (usually a lot quicker). We are also currently building out our documentation which should answer most common questions.",
            },
        },
        {
            "@type": "Question",
            name: "What devices are supported?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "For the best experience you should use Recall on desktop (Mac or PC) with our browser extensions for Chrome or Firefox. We also have mobile apps for iOS and Android which are currently in Beta.",
            },
        },
        {
            "@type": "Question",
            name: "How is Recall different from other content summarization tools?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Recall has a personal knowledge base where you can save your summaries. The summaries are automatically categorized and when you save a new summary, Recall finds relations with past content you have saved, helping you resurface information that you may have forgotten about. Recall also has features to help you revisit and learn from the summaries you have saved by using Spaced Repetition and Active Recall.",
            },
        },
        {
            "@type": "Question",
            name: "What types of online content can Recall summarize?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Recall can summarize any online content including the following: YouTube videos, Articles, PDFs, Blog posts, Podcasts, Wikipedia pages, Recipes.",
            },
        },
        {
            "@type": "Question",
            name: "Can I manually edit or add notes to a summary?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, once you add a summary to your personal knowledge base, you can edit it to your liking using our text editor.",
            },
        },
        {
            "@type": "Question",
            name: "What is Active Recall?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Active recall is the practice of actively stimulating your memory during the learning process. Instead of passively reading or listening to information, you challenge your brain to retrieve and produce the information without looking at the source. With Recall, you can practice active recall by generating questions based on the summaries in your personal knowledge base. This will really test your knowledge and help the content stick.",
            },
        },
        {
            "@type": "Question",
            name: "What is Spaced Repetition?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Spaced repetition is a learning technique that involves reviewing information at increasing intervals over time. Instead of cramming all at once (which might help in the short term but falters in the long run), you review the material multiple times, spacing out each review session. This method leverages the psychological spacing effect, wherein our brains tend to retain information better when we learn it a few times over a longer span, rather than repeatedly in a short span. With Recall, you can review the summaries you have saved on a spaced repetition schedule. You will receive a weekly email with your summaries to review or you can review them directly from the web app.",
            },
        },
        {
            "@type": "Question",
            name: "Why use Recall?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "If you're anything like us, then everyday you learn about interesting new things from blog posts and news articles you read, podcasts you listen to and videos you watch. Unfortunately most of what you learn from this content is forgotten within a couple of months. A practical solution to this is to summarize what you learn and write it down somewhere. However the problem with this approach is: 1. It is time consuming and you often just don't feel like taking down your own notes. 2. And over time you write down so much that it becomes difficult to find what you need when you need it. Recall solves these problems by automatically summarizing content and categorizing it in a consistent way. Connections are also drawn between the content you add, helping resurface existing content when it is related to something new.",
            },
        },
        {
            "@type": "Question",
            name: "Is my data secure?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, Recall is built using best practices and with a strong commitment to security and data protection. You can read more in our privacy policy.",
            },
        },
        {
            "@type": "Question",
            name: "How is my data stored?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Recall is a local-first application which means all your data is primarily stored on your device. All queries and database operations happen directly on your device making Recall fast and responsive. For backup and syncing between devices we store your data on a database hosted with Google Cloud in a data center located in Belgium (europe-west1).",
            },
        },
        {
            "@type": "Question",
            name: "Can I export my data?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, you can export all your data to markdown files. To do this, open the main app menu, click Settings and then click Export. This will download all your data into a zipped collection of markdown files.",
            },
        },
        {
            "@type": "Question",
            name: "How can I delete my account?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "To completely delete your Recall account along with all your data, open the main app menu, click Settings and then click Delete Account. Please note that this cannot be undone and your data will be permanently removed (locally and from our servers).",
            },
        },
        {
            "@type": "Question",
            name: "What happens if Recall disappears one day?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "It's understandable that you might hesitate to invest your time in entering your information into a new application, given the natural concern that it could potentially vanish one day. I want to reassure you that we are fully committed to Recall for the long haul. However, in the unlikely and unfortunate event that Recall does not succeed, we will guarantee that users have ample time to export their data so it can be transferred to another application. Additionally, we would open-source the project, enabling users to host Recall on their own.",
            },
        },
        {
            "@type": "Question",
            name: "What is on the roadmap for Recall?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "We are only just getting started and have an ambitious roadmap full of new features that are going to be added to Recall. We share some of the ones that we have planned in the next few months on our public roadmap.",
            },
        },
        {
            "@type": "Question",
            name: "Can I request a feature?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, you can submit feature requests. You can also upvote feature requests that others have already made by other users.",
            },
        },
        {
            "@type": "Question",
            name: "What payment options are supported?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "We support bank/credit cards, Apple Pay, Google Pay, Cash App, WeChat Pay and Link.",
            },
        },
        {
            "@type": "Question",
            name: "Can I use Recall for free?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, you can get started with Recall for free. In the free tier you can create 10 free AI generated summaries. After that you can continue to create as many in-app knowledge cards as you like.",
            },
        },
        {
            "@type": "Question",
            name: "What happens to my knowledge base if I cancel my subscription?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Don't worry, you will still have access to your knowledge base and you will be able to continue manually adding and editing your content. You can also continue to add content using the in-app search. You will just not be able to continue using the AI generated summaries.",
            },
        },
        {
            "@type": "Question",
            name: "How can I cancel my subscription?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "You can cancel your subscription by clicking the main app menu, then clicking on Settings. From Settings, click Manage Subscription where you can login to our subscription portal and cancel your account.",
            },
        },
        {
            "@type": "Question",
            name: "Who are the people behind Recall?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "We are a small, ambitious team based in Amsterdam. Our founders are from South Africa and Serbia.",
            },
        },
        {
            "@type": "Question",
            name: 'What does "Unlimited" mean in Recall&apos;s pricing?',
            acceptedAnswer: {
                "@type": "Answer",
                text: `Our Unlimited Plan gives you extensive access to Recall&apos;s features, but like any service, it is subject to reasonable usage limits. Excessive usage may require a review. Please see our <a href='${ROUTES.FAIR_USE_POLICY}'>Fair Use Policy</a> for details.`,
            },
        },
        {
            "@type": "Question",
            name: " Where can I find the full Terms of Service and Fair Use Policy?",
            acceptedAnswer: {
                "@type": "Answer",
                text: `You can read our full <a href='${ROUTES.TERMS_OF_SERVICE}'>Terms of Service</a> and <a href='${ROUTES.FAIR_USE_POLICY}'>Fair Use Policy</a> for details on usage, payments, and service policies.`,
            },
        },
        {
            "@type": "Question",
            name: "Do you offer refunds?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, if you are not satisfied with our product within the first 30 days, we will refund you.",
            },
        },
        {
            "@type": "Question",
            name: "Student Discounts?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, please email us at support@getrecall.ai from your student email address for a 20% student discount.",
            },
        },
        {
            "@type": "Question",
            name: "What language do you support?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "You can translate AI summaries and your quiz questions to any of the following languages: English, German, French, Spanish, Italian, Chinese, Japanese, Korean, Portuguese, Russian.",
            },
        },
        {
            "@type": "Question",
            name: "Do you have a referral programme?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes! For each unique user you invite, who signs up and verifies their email, you will receive an additional 10% discount on Recall Plus - this can go up to 50% off. You can find your invite code in the top right corner of your profile page.",
            },
        },
        {
            "@type": "Question",
            name: "Do you have an affiliate programme?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, you can learn more about it here: https://sites.google.com/getrecall.ai/recall-affiliate-programme/home",
            },
        },
    ],
}
